import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Link as LinkIcon, FileVideo, Globe2, Sparkles, ChevronDown, ExternalLink,
  FileText, Image as ImageIcon, Presentation, Download,
} from "lucide-react";
import { API } from "../lib/api";

const ICON_FOR = {
  video: FileVideo,
  pdf: FileText,
  image: ImageIcon,
  presentation: Presentation,
  link: LinkIcon,
};

const LABEL_FOR = {
  video: "Video",
  pdf: "PDF",
  image: "Image",
  presentation: "Presentation",
  link: "Link",
};

// File materials are stored as "/api/files/videos/<uuid>.<ext>" and our axios
// baseURL already ends in "/api" — strip the leading "/api" before joining.
const absoluteUrl = (m) => (m.kind === "file" ? `${API}${m.url.replace(/^\/api/, "")}` : m.url);

const toEmbed = (u) => {
  if (!u) return u;
  try {
    const url = new URL(u);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (url.pathname === "/watch") {
        const v = url.searchParams.get("v");
        if (v) return `https://www.youtube.com/embed/${v}`;
      }
      if (url.pathname.startsWith("/shorts/")) {
        return `https://www.youtube.com/embed/${url.pathname.split("/")[2]}`;
      }
    }
    if (host === "youtu.be") return `https://www.youtube.com/embed/${url.pathname.slice(1)}`;
    if (host === "vimeo.com" && /^\/\d+$/.test(url.pathname)) {
      return `https://player.vimeo.com/video/${url.pathname.slice(1)}`;
    }
  } catch {
    /* original URL is fine */
  }
  return u;
};

/**
 * Renders the appropriate viewer for the material type.
 * Auto-detects playable content from the URL regardless of the saved
 * material_type — so a YouTube/Vimeo or .mp4/.webm link saved as "link"
 * still plays inline. PDF/image extensions are also auto-detected.
 */
const MaterialPlayer = ({ material }) => {
  const url = absoluteUrl(material);
  const declared = material.material_type || "video";

  // URL-pattern auto-detection
  const isYouTubeOrVimeo =
    /(youtube\.com\/(watch\?|embed\/|shorts\/)|youtu\.be\/|player\.vimeo\.com\/video\/|vimeo\.com\/\d+)/i.test(url);
  const isDirectVideo = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(url);
  const isDirectPdf   = /\.pdf(\?|#|$)/i.test(url);
  const isDirectImg   = /\.(jpe?g|png|gif|webp|svg)(\?|#|$)/i.test(url);

  // Effective type — prefer URL signals when available, else use declared.
  let mt = declared;
  if (isYouTubeOrVimeo || isDirectVideo) mt = "video";
  else if (isDirectPdf) mt = mt === "presentation" ? "presentation" : "pdf";
  else if (isDirectImg) mt = "image";
  else if (declared === "video" && material.kind === "url" && !isDirectVideo && !isYouTubeOrVimeo) {
    // Declared video but URL is neither — fall back to opening as a link
    mt = "link";
  }

  if (mt === "video") {
    const resolved = material.kind === "file" ? url : toEmbed(material.url);
    const isEmbed = isYouTubeOrVimeo;
    if (isEmbed) {
      return (
        <iframe
          src={toEmbed(material.url)}
          title={material.title || "Video"}
          className="aspect-video w-full rounded-xl border border-slate-200 bg-black"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid={`video-iframe-${material.id}`}
        />
      );
    }
    return (
      <video
        controls
        preload="metadata"
        className="aspect-video w-full rounded-xl border border-slate-200 bg-black"
        data-testid={`video-player-${material.id}`}
      >
        <source src={resolved} />
        Your browser does not support video playback.
      </video>
    );
  }

  if (mt === "image") {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={`image-link-${material.id}`}>
        <img
          src={url}
          alt={material.title || "Image"}
          className="max-h-[600px] w-full rounded-xl border border-slate-200 bg-slate-50 object-contain"
          data-testid={`image-${material.id}`}
        />
      </a>
    );
  }

  if (mt === "pdf" || mt === "presentation") {
    if (isDirectPdf || mt === "pdf") {
      return (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <iframe
            src={url}
            title={material.title || LABEL_FOR[mt]}
            className="aspect-[4/3] w-full"
            data-testid={`pdf-iframe-${material.id}`}
          />
        </div>
      );
    }
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-5 transition hover:bg-slate-100"
        data-testid={`presentation-link-${material.id}`}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100 text-violet-600">
          <Presentation size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">{material.title || material.original_filename || "Presentation"}</p>
          <p className="text-xs text-slate-500">Click to open / download</p>
        </div>
        <Download className="text-slate-400" size={18} />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-5 transition hover:bg-slate-100"
      data-testid={`external-link-${material.id}`}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
        <ExternalLink size={22} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-900">{material.title || material.url}</p>
        <p className="truncate text-xs text-slate-500">{material.url}</p>
      </div>
      <ExternalLink className="text-slate-400" size={18} />
    </a>
  );
};

/**
 * Choose the best material for the user's selected language.
 * Falls back to English, then to the first available.
 */
const pickPreferred = (materials, prefLangCode) => {
  if (!materials?.length) return null;
  const codeToName = { en: "english", hi: "hindi", as: "assamese" };
  const target = (codeToName[prefLangCode] || prefLangCode || "").toLowerCase();
  const exact = materials.find((v) => (v.language || "").toLowerCase() === target);
  if (exact) return exact;
  const eng = materials.find((v) => (v.language || "").toLowerCase() === "english");
  if (eng) return eng;
  return materials[0];
};

export const SubmoduleViewer = ({ submodule, prefLangCode, onContentView }) => {
  const [activeId, setActiveId] = useState(() => pickPreferred(submodule.videos, prefLangCode)?.id);
  const active = useMemo(
    () => submodule.videos?.find((v) => v.id === activeId) || null,
    [submodule.videos, activeId]
  );
  const viewSent = useRef(false);

  // Auto-pick when the user changes their UI language
  useEffect(() => {
    const pref = pickPreferred(submodule.videos, prefLangCode);
    if (pref) setActiveId(pref.id);
  }, [prefLangCode, submodule.videos]);

  // Record that the student has viewed content in this sub-section (once).
  useEffect(() => {
    if (onContentView && active && !viewSent.current) {
      viewSent.current = true;
      onContentView(submodule.id);
    }
  }, [active, onContentView, submodule.id]);

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card" data-testid={`submodule-viewer-${submodule.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{submodule.name}</h3>
          {submodule.outcomes?.length > 0 && (
            <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-slate-600">
              {submodule.outcomes.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          )}
        </div>
      </div>

      {submodule.videos?.length ? (
        <div className="mt-4 space-y-3">
          {/* Material pills (per-language + per-type) */}
          <div className="flex flex-wrap items-center gap-1.5">
            {submodule.videos.map((v) => {
              const Icon = ICON_FOR[v.material_type || "video"] || FileVideo;
              return (
                <button
                  key={v.id}
                  onClick={() => setActiveId(v.id)}
                  data-testid={`video-pill-${v.id}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    v.id === activeId
                      ? "border-violet-300 bg-violet-50 text-violet-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Globe2 size={11} /> {v.language}
                  <span className="text-slate-400">·</span>
                  <Icon size={11} /> {LABEL_FOR[v.material_type || "video"]}
                </button>
              );
            })}
          </div>

          {active && (
            <>
              <MaterialPlayer material={active} />
              {(active.title || active.kind === "url") && (
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="truncate font-medium text-slate-700">{active.title || ""}</span>
                  {active.kind === "url" && (
                    <a
                      href={active.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                    >
                      Open <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <p className="mt-3 rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-500">
          No materials uploaded yet for this submodule.
        </p>
      )}
    </div>
  );
};

/**
 * Full module-detail body — renders the module header with outcomes/objectives
 * and a list of SubmoduleViewer cards.
 */
export const ModuleDetailBody = ({ data, prefLangCode, onContentView }) => {
  const { module, submodules } = data;

  return (
    <>
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <h2 className="text-2xl font-extrabold text-slate-900">{module.name}</h2>
        {module.objectives?.length > 0 && (
          <details className="mt-4" open>
            <summary className="cursor-pointer text-sm font-semibold text-slate-700">
              <ChevronDown size={14} className="mr-1 inline" /> Objectives
            </summary>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
              {module.objectives.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </details>
        )}
        {module.outcomes?.length > 0 && (
          <div className="mt-4 rounded-xl bg-emerald-50/60 p-4">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
              <Sparkles size={12} /> Learning Outcomes
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-emerald-900">
              {module.outcomes.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-6 space-y-4">
        {submodules.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center">
            <FileVideo size={32} className="mx-auto text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-700">No submodules yet</p>
            <p className="mt-1 text-xs text-slate-500">Submodules and learning materials will appear here once your teacher or admin adds them.</p>
          </div>
        ) : (
          submodules.map((s) => (
            <SubmoduleViewer key={s.id} submodule={s} prefLangCode={prefLangCode} onContentView={onContentView} />
          ))
        )}
      </div>
    </>
  );
};
